import { useState } from 'react';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { Plus, FileText, Reply, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { EmptyState } from '@/components/ui/empty-state';
import {
  useRfiListQuery,
  useRespondRfiMutation,
  useCloseRfiMutation,
  RFI_RESOLUTION_OUTCOMES,
  type RfiResolutionOutcome,
  type RfiSummary,
} from '@/lib/api/rfis';
import { RfiStatusChip } from '@/components/rfis/rfi-status-chip';
import { formatDate, titleCase } from '@/lib/utils/format';

/**
 * RFIs screen (T-0101 / E04-F04).
 *
 * Per prototype `screens-rfi.js`: a vertical list of cards, each showing
 *
 *   - RFI number (mono) + title
 *   - state-machine chip (open / responded / closed / escalated)
 *   - recipient kind + opened-at + responded/closed dates
 *   - response body when present (highlighted inline card)
 *
 * The "Create RFI" affordance carries `data-create="rfi"` — routed by the
 * global EditorModalHost into the create modal, which posts to the
 * dedicated RFI endpoint. Respond / Close / Escalate actions render
 * per-card, gated by the RFI state machine.
 */

export const Route = createFileRoute('/engagements/$engagementId/rfis')({
  component: RfisTab,
});

function RfisTab() {
  const { engagementId } = useParams({ from: '/engagements/$engagementId/rfis' });
  const list = useRfiListQuery(engagementId);

  const items = list.data?.items ?? [];
  const counts = {
    open: items.filter((r) => r.status === 'open').length,
    responded: items.filter((r) => r.status === 'response_received').length,
    closed: items.filter((r) => r.status === 'closed').length,
    escalated: items.filter((r) => r.status === 'escalated').length,
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-h1 font-semibold text-ink-900">RFI lifecycle</h1>
          <p className="text-meta text-ink-500">
            Open → Sent → Responded → Closed. SLA-tracked per engagement; AI-extracted from
            interviews and chat where applicable.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 border-r border-ink-100 pr-3">
            {counts.escalated > 0 ? (
              <RfiStatusChip status="escalated">{counts.escalated} escalated</RfiStatusChip>
            ) : null}
            <RfiStatusChip status="open">{counts.open} open</RfiStatusChip>
            <RfiStatusChip status="response_received">{counts.responded} responded</RfiStatusChip>
            <RfiStatusChip status="closed">{counts.closed} closed</RfiStatusChip>
          </div>
          <Button
            type="button"
            data-create="rfi"
            size="sm"
            className="bg-accent-amber-600 text-paper-0 hover:bg-accent-amber-700"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            New RFI
          </Button>
        </div>
      </div>

      {list.isLoading ? (
        <div className="flex items-center gap-2 text-body-sm text-ink-500">
          <Spinner /> Loading RFIs…
        </div>
      ) : list.error ? (
        <ErrorMessage error={list.error} />
      ) : items.length === 0 ? (
        <EmptyState
          title="No RFIs yet"
          description="When the team raises a formal information request, it'll appear here. Use the New RFI button above to draft one."
        />
      ) : (
        <ul className="flex flex-col gap-3" role="list">
          {items.map((rfi) => (
            <RfiCard key={rfi.id} rfi={rfi} engagementId={engagementId} />
          ))}
        </ul>
      )}
    </div>
  );
}

function RfiCard({ rfi, engagementId }: { rfi: RfiSummary; engagementId: string }) {
  // The list endpoint returns summary metadata; the prototype card also
  // shows the body + linked-doc citations. We expose the metadata we have
  // and leave deep detail for a future RFI detail route.
  const accent =
    rfi.status === 'escalated'
      ? 'border-l-posture-critical'
      : rfi.status === 'closed'
        ? 'border-l-posture-healthy'
        : rfi.status === 'response_received'
          ? 'border-l-accent-amber-600'
          : 'border-l-ai-surface-border';

  return (
    <li>
      <Card className={`border-l-4 ${accent}`}>
        <CardContent className="space-y-2 p-4">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="font-mono text-meta text-ink-500">{rfi.rfi_number}</span>
            <span className="flex-1 truncate text-body font-semibold text-ink-900">
              {rfi.title}
            </span>
            <RfiStatusChip status={rfi.status} />
          </div>
          <div className="text-meta text-ink-500">
            Sent to {titleCase(rfi.recipient_kind)} · Opened {formatDate(rfi.opened_at)}
            {rfi.responded_at ? <> · Responded {formatDate(rfi.responded_at)}</> : null}
            {rfi.closed_at ? <> · Closed {formatDate(rfi.closed_at)}</> : null}
          </div>
          {rfi.resolution_outcome ? (
            <div className="flex items-center gap-2 text-meta text-ink-700">
              <FileText className="h-3 w-3" aria-hidden="true" />
              <span>Resolution: {titleCase(rfi.resolution_outcome)}</span>
            </div>
          ) : null}
          <RfiActions rfi={rfi} engagementId={engagementId} />
        </CardContent>
      </Card>
    </li>
  );
}

/**
 * Lifecycle actions per the RFI state machine:
 *   open → Record response (response_text) → response_received
 *   response_received → Close (resolved | needs_further_discussion) or
 *                       Escalate — both via the close endpoint's
 *                       resolution_outcome; escalated maps to its own
 *                       terminal status.
 */
function RfiActions({ rfi, engagementId }: { rfi: RfiSummary; engagementId: string }) {
  const [mode, setMode] = useState<'idle' | 'respond' | 'close'>('idle');
  const [responseText, setResponseText] = useState('');
  const [outcome, setOutcome] = useState<RfiResolutionOutcome>('resolved');
  const [error, setError] = useState<string | null>(null);
  const respond = useRespondRfiMutation(engagementId);
  const close = useCloseRfiMutation(engagementId);

  if (rfi.status !== 'open' && rfi.status !== 'response_received') return null;

  const pending = respond.isPending || close.isPending;
  const onError = (e: unknown) =>
    setError(e instanceof Error ? e.message : 'Action failed — try again.');

  if (mode === 'idle') {
    return (
      <div className="flex items-center gap-2 pt-1">
        {rfi.status === 'open' ? (
          <Button type="button" variant="outline" size="sm" onClick={() => setMode('respond')}>
            <Reply className="h-3.5 w-3.5" aria-hidden="true" />
            Record response
          </Button>
        ) : (
          <Button type="button" variant="outline" size="sm" onClick={() => setMode('close')}>
            <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
            Close RFI
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2 rounded-card border border-ink-100 bg-paper-50 p-3">
      {mode === 'respond' ? (
        <>
          <label className="block text-meta font-medium text-ink-700">
            Response received from {titleCase(rfi.recipient_kind)}
            <textarea
              value={responseText}
              onChange={(e) => setResponseText(e.target.value)}
              rows={3}
              placeholder="Paste or summarize the response…"
              className="mt-1 w-full rounded-card border border-ink-200 bg-paper-0 px-2 py-1.5 text-body-sm text-ink-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300"
            />
          </label>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              disabled={pending || responseText.trim().length === 0}
              onClick={() => {
                setError(null);
                respond.mutate(
                  {
                    rfiId: rfi.id,
                    responseText: responseText.trim(),
                    idempotencyKey: crypto.randomUUID(),
                  },
                  { onSuccess: () => setMode('idle'), onError },
                );
              }}
            >
              {respond.isPending ? <Spinner /> : null}
              Save response
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setMode('idle')}>
              Cancel
            </Button>
          </div>
        </>
      ) : (
        <>
          <label className="block text-meta font-medium text-ink-700">
            Resolution outcome
            <select
              value={outcome}
              onChange={(e) => setOutcome(e.target.value as RfiResolutionOutcome)}
              className="mt-1 w-full rounded-card border border-ink-200 bg-paper-0 px-2 py-1.5 text-body-sm text-ink-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300"
            >
              {RFI_RESOLUTION_OUTCOMES.map((o) => (
                <option key={o} value={o}>
                  {titleCase(o)}
                </option>
              ))}
            </select>
          </label>
          <p className="text-meta text-ink-500">
            Resolved and Needs-further-discussion close the RFI; Escalated moves it to the escalated
            state for leadership follow-up.
          </p>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              disabled={pending}
              onClick={() => {
                setError(null);
                close.mutate(
                  { rfiId: rfi.id, outcome, idempotencyKey: crypto.randomUUID() },
                  { onSuccess: () => setMode('idle'), onError },
                );
              }}
            >
              {close.isPending ? <Spinner /> : null}
              {outcome === 'escalated' ? 'Escalate' : 'Close'}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setMode('idle')}>
              Cancel
            </Button>
          </div>
        </>
      )}
      {error ? <p className="text-meta font-medium text-cite-broken">{error}</p> : null}
    </div>
  );
}

// Re-export for tests / shell wiring per T-0101 scope.
export { RfisTab };
